/**
 * @file: The default times of a new interval.
 *
 * Three routes create an interval without a drag. They are the toolbar button,
 * the grid menu and the takeover button in the panel. Each route must open with
 * the same length, or the operator gets a different result for the same action.
 * The lengths and the rules that place them are in this file for that reason.
 */

import moment from 'moment'
import { SNAP_MINUTES } from './grid-geometry'

/**
 * A new takeover opens for half an hour. The grid menu and the takeover button
 * in the panel both use this length. To drag an edge out is one action. A large
 * window that the operator must first drag in covers the interval below it.
 */
const TAKEOVER_MINUTES = 30

/** A new interval opens for a full hour. The toolbar and the grid menu use the same length. */
const NEW_INTERVAL_MINUTES = 60

/** A new interval from the toolbar starts at this hour on a day that is not today. */
const NEW_INTERVAL_HOUR = 12

/**
 * Move a moment onto the snap grid of the calendar, so the panel offers the
 * times that a drag gives. Pass Math.floor or Math.ceil as `round`.
 */
function onSnapGrid (value, round) {
  const hour = moment(value).startOf('hour')
  const minutes = moment(value).diff(hour, 'minutes', true)

  return hour.add(round(minutes / SNAP_MINUTES) * SNAP_MINUTES, 'minutes')
}

/**
 * The position of a takeover that starts over one interval.
 *
 * The takeover opens for TAKEOVER_MINUTES in the middle of that interval. The
 * operator therefore starts with a window inside the stretch that it takes
 * over, and can then drag either edge. The times move onto the quarter hour
 * that the grid snaps to.
 *
 * An interval with no room for a quarter-aligned window of that length gives
 * its whole stretch, unchanged. The operator already knows those edges. To round
 * them would move the takeover outside the interval.
 *
 * @returns {Object|null} { start, end } as Dates, or null for unreadable times
 */
function takeoverWindow (start, end) {
  const from = moment(start)
  const to = moment(end)

  if (!start || !end || !from.isValid() || !to.isValid() || !to.isAfter(from)) {
    return null
  }

  const earliest = onSnapGrid(from, Math.ceil)
  const latest = onSnapGrid(to, Math.floor)

  if (latest.diff(earliest, 'minutes') < TAKEOVER_MINUTES) {
    return { start: from.toDate(), end: to.toDate() }
  }

  const centred = onSnapGrid(
    moment(from.valueOf() + (to.diff(from) / 2)).subtract(TAKEOVER_MINUTES / 2, 'minutes'),
    Math.round)
  const last = latest.clone().subtract(TAKEOVER_MINUTES, 'minutes')
  const opening = moment.min(moment.max(centred, earliest), last)

  return {
    start: opening.toDate(),
    end: opening.clone().add(TAKEOVER_MINUTES, 'minutes').toDate()
  }
}

/**
 * The start of a new interval that has no times from a drag.
 *
 * The panel opens on the current hour when the grid shows the present moment.
 * Otherwise it opens at noon on the first day on show. A start outside the
 * range on show gave a bar in another week, and the operator saw nothing.
 *
 * @param {Object} now - A moment
 * @param {Object|null} range - { start, end } that the grid shows
 * @returns {Object} A moment
 */
function defaultNewStart (now, range) {
  const shown = range && !now.isBefore(range.start) && now.isBefore(range.end)

  if (!range || shown) {
    return now.clone().startOf('hour')
  }

  return moment(range.start).startOf('day').hour(NEW_INTERVAL_HOUR)
}

export {
  TAKEOVER_MINUTES,
  NEW_INTERVAL_MINUTES,
  takeoverWindow,
  defaultNewStart
}
